import { useMutation, useQueryClient } from "@tanstack/react-query";
import { authService } from "../../../api/services/auth";
import type { RequestAuthRequest, VerifyAuthRequest, SocialProvider } from "../../../api/services/auth";

export const useRequestAuth = () => {
  return useMutation({
    mutationFn: (data: RequestAuthRequest) => authService.requestAuth(data),
  });
};


export const useVerifyAuth = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: VerifyAuthRequest) => authService.verifyAuth(data),
    onSuccess: () => {
      // Refetch user profile after successful verification
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      console.log('[useLogout] 🚀 Starting logout...');
      console.log('[useLogout] Cookies before logout:', document.cookie);
      const response = await authService.logout();
      console.log('[useLogout] Backend logout response:', response);
      return response;
    },
    onSuccess: () => {
      console.log('[useLogout] ✅ Logout succeeded');
      console.log('[useLogout] Cookies after logout:', document.cookie);
      // Clear all cached queries on logout
      queryClient.clear();
    },
    onError: (error: any) => {
      console.error('[useLogout] ❌ Logout failed:', {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
      console.log('[useLogout] Cookies after failed logout:', document.cookie);
    },
  });
};

export const useSocialLogin = () => {
  return useMutation({
    mutationFn: ({ provider, redirectUrl }: { provider: SocialProvider; redirectUrl?: string }) =>
      authService.socialLogin(provider, redirectUrl),
    // Don't refetch profile here - user hasn't authenticated yet
    // Profile will be fetched after OAuth callback completes
    // NEVER cache this - OAuth links are unique each time
    gcTime: 0,
    retry: false,
  });
};

export const useSocialCallback = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ provider, code, state }: { provider: SocialProvider; code: string; state: string }) => {
      console.log(`[useSocialCallback] 🚀 Starting ${provider} callback exchange...`);
      console.log(`[useSocialCallback] Request details:`, {
        provider,
        codeLength: code.length,
        stateLength: state.length,
      });
      try {
        const response = await authService.socialCallback(provider, code, state);
        console.log(`[useSocialCallback] ✅ Backend response received:`, response);
        return response;
      } catch (error: any) {
        console.error(`[useSocialCallback] ❌ Callback exchange failed:`, {
          status: error?.response?.status,
          statusText: error?.response?.statusText,
          data: error?.response?.data,
          url: error?.config?.url,
        });
        throw error;
      }
    },
    onSuccess: (response) => {
      console.log('[useSocialCallback] ✅ Social callback succeeded, invalidating profile cache');
      
      // Immediately invalidate the profile query to force refetch
      queryClient.invalidateQueries({ 
        queryKey: ['profile'],
        refetchType: 'all', // Refetch all active queries with this key
      });
      
      // Also clear the cached auth_state from localStorage to ensure fresh fetch
      try {
        localStorage.removeItem('auth_state');
        console.log('[useSocialCallback] Cleared cached auth_state');
      } catch (e) {
        console.warn('[useSocialCallback] Could not clear auth_state:', e);
      }
    },
    onError: (error: any) => {
      console.error('[useSocialCallback] ❌ Social callback failed:', error);
      console.error('[useSocialCallback] Error details:', {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
    },
  });
};
